<template>
    <div class="overflow-auto">
        <div v-if="isLoading" class="d-flex justify-content-center mt-3">
            <FcoSpinner />
        </div>
        <div v-else>
            <!-- The perPage is set to recentVehicles.length + 1 to make sure we always have at least one page and no pagination -->
            <FcoTable
                :tableClass="{ selectable: hasVehicles }"
                :tbodyClass="{ 'font-italic': !hasVehicles }"
                :data="recentVehicles"
                @click-row="handleVehicleClick($event.id)"
                :perPage="recentVehicles.length + 1"
                :noResultsText="fcoM('rs.homeMain.noVehicles', 'You have no recent vehicles.')"
            >
                <!-- Date -->
                <FcoTableColumn v-if="$fcoMq.isSmUp" :label="fcoM('common.date', 'Date')" :sortable="false">
                    <template #td="recentVehicle">
                        <span v-if="formatDate(recentVehicle.createDateTime) !== ''">{{ formatDate(recentVehicle.createDateTime) }}</span>
                        <span v-else>{{ fcoM('common.na', 'N/A') }}</span>
                    </template>
                </FcoTableColumn>

                <!-- Vin -->
                <FcoTableColumn v-if="$fcoMq.isMdUp" class="text-nowrap" :label="fcoM('common.vin', 'VIN')">
                    <template #td="recentVehicle">
                        <span v-if="recentVehicle.vin" class="text-uppercase">{{ recentVehicle.vin }}</span>
                        <span v-else>{{ fcoM('common.na', 'N/A') }}</span>
                        <template v-if="recentVehicle.sourceVinScanner">
                            <Icon class="ml-1 text-lg" name="mobile" />
                            <span
                                v-if="recentVehicle.vehicleNotes && recentVehicle.vehicleNotes !== ''"
                                v-fco-tooltip="{ content: recentVehicle.vehicleNotes, container: $el }"
                            >
                                <Icon name="pencil-square-o" class="ml-1 clickable text-lg" />
                            </span>
                        </template>
                    </template>
                </FcoTableColumn>

                <!-- License Plate -->
                <FcoTableColumn v-if="$fcoMq.isMdUp" :label="fcoM('common.vehicleLicensePlate', 'License Plate #')">
                    <template #td="recentVehicle">
                        <span v-if="licensePlate(recentVehicle) !== ''">{{ licensePlate(recentVehicle) }}</span>
                        <span v-else>{{ fcoM('common.na', 'N/A') }}</span>
                    </template>
                </FcoTableColumn>

                <!-- State -->
                <FcoTableColumn v-if="$fcoMq.isMdUp" :label="fcoM('common.state', 'State')">
                    <template #td="recentVehicle">
                        <span v-if="plateState(recentVehicle) !== ''">{{ plateState(recentVehicle) }}</span>
                        <span v-else>{{ fcoM('common.na', 'N/A') }}</span>
                    </template>
                </FcoTableColumn>

                <!-- Vehicle -->
                <FcoTableColumn class="text-nowrap" :label="fcoM('common.vehicle', 'Vehicle')">
                    <template #td="recentVehicle">
                        {{ vehicle(recentVehicle) }}
                    </template>
                </FcoTableColumn>

                <!-- Delete Recent Vehicle -->
                <FcoTableColumn tdClass="delete-vehicle center">
                    <template #td="recentVehicle">
                        <!-- Need to put the @click event on a div wrapper or else the event doesn't fire properly -->
                        <div @click.stop="deleteVehicle(recentVehicle.id)">
                            <Icon name="times" class="clickable red" />
                        </div>
                    </template>
                </FcoTableColumn>
            </FcoTable>
        </div>
        <QuoteSelectionModal
            v-if="showNewQuotePrompt"
            @close="showNewQuotePrompt = false"
            @start-new-quote="handleQuotePromptAnswer(false)"
            @use-existing-quote="handleQuotePromptAnswer(true)"
        />
    </div>
</template>

<script>
import { mapState } from 'vuex';
import { fcoTooltip } from 'fco/src/vue/directives/tooltip';
import vsMixin from '@/common/mixins/vsMixin';
import { FcoTable, FcoTableColumn } from 'fco/src/vue/components/table';
import { isRequestIdle, requestSettled } from '@/common/store/request-status';
import Icon from './Icon.vue';
import QuoteSelectionModal from './vehicleSelector/QuoteSelectionModal.vue';
import featureFlagsMixin from '../mixins/featureFlagsMixin';
import { VehicleSelectionMethod } from '../constants/analytics';

const dateFormatter = new Intl.DateTimeFormat('en-US');

export default {
    name: 'RecentVehicles',
    mixins: [vsMixin, featureFlagsMixin],
    data() {
        return {
            isLoading: false,
            tempVehicle: null,
            showNewQuotePrompt: false,
        };
    },
    computed: {
        ...mapState({
            requests: (state) => state.requests,
            currentShopId: ({ currentShop }) => currentShop?.id,
        }),
        recentVehicles() {
            return this.$store.state.vehicleSelector.recentVehicles;
        },
        hasVehicles() {
            return !this.isLoading && this.recentVehicles.length > 0;
        },
    },
    directives: { fcoTooltip },
    components: {
        Icon,
        FcoTable,
        FcoTableColumn,
        QuoteSelectionModal,
    },
    methods: {
        async deleteVehicle(vehicleId) {
            try {
                await this.$store.dispatch('vehicleSelector/deleteRecentVehicle', vehicleId);
            } catch {
                this.$fcoToast.error(this.fcoM('rs.homeMain.errorRemovingVehicle', 'There was an error removing the vehicle, please try again later'));
            }
        },
        handleQuotePromptAnswer(keepExistingQuote) {
            this.userWantsExistingQuote = keepExistingQuote;
            this.showNewQuotePrompt = false;
            this.handleVehicleClick(this.tempVehicle, true);
        },
        handleVehicleClick(vehicleId, ignoreQuoteCheck = false) {
            if (this.productsInCurrentQuote && this.promptAboutQuote && !ignoreQuoteCheck) {
                this.showNewQuotePrompt = true;
                this.tempVehicle = vehicleId;
                return;
            }

            this.selectVehicle(vehicleId);
        },
        async selectVehicle(vehicleId) {
            this.isLoading = true;
            try {
                const vehicle = await this.selectByWorksheetVehicleId(vehicleId, this.keepExistingQuoteFinal);
                this.handleVehicleSelectSuccess({
                    vehicle,
                    method: VehicleSelectionMethod.RECENT_VEHICLES,
                });
                if (this.preventNavigationOnVehicleUpdate) {
                    this.isLoading = false;
                }
            } catch (error) {
                this.$fcoToast.error(this.fcoM('rs.homeMain.errorLoadingVehicle', 'There was an error loading the vehicle, please try again later'));
                this.isLoading = false;
            }
        },
        formatDate(dateValue) {
            if (!dateValue) return '';
            return dateFormatter.format(dateValue);
        },
        licensePlate(recentVehicle) {
            return recentVehicle.plate || '';
        },
        plateState(recentVehicle) {
            return recentVehicle.state || '';
        },
        vehicle(recentVehicle) {
            return `${recentVehicle.year} ${recentVehicle.make} ${recentVehicle.model}`;
        },
    },
    async mounted() {
        this.isLoading = true;
        if (isRequestIdle(this.requests.getCurrentShop)) this.$store.dispatch('getCurrentShop');
        await requestSettled(() => this.requests.getCurrentShop);
        try {
            await this.$store.dispatch('vehicleSelector/getRecentVehicles', this.currentShopId);
        } catch {
            this.$fcoToast.error(this.fcoM('rs.homeMain.errorLoadingRecentVehicles', 'There was an error loading recent vehicles, please try again later'));
        }
        this.isLoading = false;
    },
};
</script>

<style lang="scss">
.delete-vehicle {
    width: 5%;
}
</style>
